/**
 * Section Cursor Domain
 * Tracks real-time cursor positions of teammates in the same game section.
 * Section cursor uses the section presence system as an implicit
 * subscription — players in the same section automatically see each other's
 * cursors.
 *
 * Coordinate semantics vary by section:
 * - Default: x/y are normalized 0–1 fractions of the container.
 * - Globe: x = latitude, y = longitude.
 * - Innovation: x/y are board-space cell coordinates relative to the tech-tree
 *   origin. These may be outside the occupied 11x11 tree when the cursor is in
 *   padded canvas space.
 * Sections register CoordTransforms (cursor-config.ts) to use content-space coords.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import { sendToMatch } from '../services/nakama/match-send';

// ==================== TYPES ====================

/** Cursor position data for a teammate. Coordinate semantics depend on section
 *  (see module doc). The server relays these opaquely — interpretation is client-side. */
export interface CursorPosition {
  user_id: string;
  /** X position: normalized 0–1 fraction for non-globe sections, latitude for globe */
  x: number;
  /** Y position: normalized 0–1 fraction for non-globe sections, longitude for globe */
  y: number;
}

// ==================== STORE ====================

interface SectionCursorState {
  /** Map of user_id → cursor position for teammates in the same section */
  teammateCursors: Record<string, CursorPosition>;

  /** Update a teammate's cursor position */
  updateCursor: (userId: string, x: number, y: number) => void;

  /** Remove a teammate's cursor (on leave or section change) */
  removeCursor: (userId: string) => void;

  /** Every cursor gone, as a session starts: on a section change, and in the full reset */
  reset: () => void;
}

const initialState = {
  teammateCursors: {} as Record<string, CursorPosition>,
};

/**
 * Section Cursor Store
 * Manages cursor positions for teammates in the same section
 */
export const useSectionCursorStore = create<SectionCursorState>()(
  devtools(
    (set) => ({
      ...initialState,

      updateCursor: (userId, x, y) => {
        set(
          (state) => ({
            teammateCursors: {
              ...state.teammateCursors,
              [userId]: { user_id: userId, x, y },
            },
          }),
          false,
          'updateCursor'
        );
      },

      removeCursor: (userId) => {
        set(
          (state) => {
            const newCursors = { ...state.teammateCursors };
            delete newCursors[userId];
            return { teammateCursors: newCursors };
          },
          false,
          'removeCursor'
        );
      },

      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    { name: 'section-cursor-store' }
  )
);

// ==================== HANDLERS ====================

/**
 * Handle incoming teammate cursor position from server
 * Server broadcasts this to all same-section teammates (excluding sender)
 */
export function handleSectionCursorPosition(data: {
  type: string;
  user_id: string;
  x: number;
  y: number;
}) {
  useSectionCursorStore.getState().updateCursor(data.user_id, data.x, data.y);
}

/**
 * A teammate's cursor goes: they left the match (players.ts), or moved to
 * another section or opened a city's panel, where their face shows instead
 * (section-presence.ts).
 */
export function handleTeammateCursorGone(userId: string): void {
  useSectionCursorStore.getState().removeCursor(userId);
}

// ==================== SERVICE METHODS ====================

/**
 * Sends the current player's cursor position to the server.
 * Coordinate semantics are section-defined:
 * - Most sections use normalized fractions (0–1) of the container.
 * - Globe uses latitude/longitude.
 * - Innovation uses board-space cell coordinates relative to the tech-tree origin.
 */
export async function sendSectionCursorPosition(x: number, y: number): Promise<void> {
  const message = {
    type: MessageType.MessageTypeSectionCursorPosition,
    x,
    y,
  };

  await sendToMatch(OpCode.OpCodeSectionCursorPosition, message, 'a cursor position');
}

// ==================== THE PLAYER'S ACTS ====================

/** The player changed section: the teammates' cursors drawn in the one they left go. */
export function clearTeammateCursors(): void {
  useSectionCursorStore.getState().reset();
}
